import React from 'react';
import { CiViewTable } from 'react-icons/ci';
import { AiOutlinePieChart } from 'react-icons/ai';
import { MdOutlineShowChart } from 'react-icons/md';
import { BsBarChart } from 'react-icons/bs';
import { ColumnTooltipIcon } from '@/features/ai-agents/chat/MarkdownTableVisualization/ColumnTooltipIcon';

import {
  AxisControls,
  AxisLabelText,
  AxisSegment,
  AxisSelect,
  AxisTooltipWrapper,
  Controls,
  ViewButton,
  ViewControls,
  VisuallyHidden,
} from '@/features/ai-agents/chat/MarkdownTableVisualization/styles';
import {
  ChartView,
  TableViewType,
  TooltipTextResolver,
} from '@/features/ai-agents/chat/MarkdownTableVisualization/types';

interface ChartViewOption {
  label: string;
  value: ChartView;
}

interface VisualizationControlsProps {
  activeView: TableViewType;
  availableBreakdownColumnIndexes: number[];
  availableChartViews: ChartViewOption[];
  availableXColumnIndexes: number[];
  canShowChart: boolean;
  chartNumericColumnIndexes: number[];
  getTooltip?: TooltipTextResolver;
  headers: string[];
  onSelectBreakdownColumnIndex: (columnIndex: number) => void;
  onSelectView: (view: TableViewType) => void;
  onSelectXColumnIndex: (columnIndex: number) => void;
  onSelectYColumnIndex: (columnIndex: number) => void;
  orderedColumnIndexes: number[];
  selectedBreakdownColumnIndex: number;
  selectedXColumnIndex: number;
  selectedYColumnIndex: number;
}

const getChartButtonTitle = (canShowChart: boolean, label: string) =>
  canShowChart
    ? `Show ${label.toLowerCase()} chart`
    : `A chart requires a numeric column`;

const getChartButtonPosition = (chartView: ChartView) => {
  switch (chartView) {
    case `line`:
      return 2;
    case `bar`:
      return 1;
    case `pie`:
      return 3;
    default:
      return 99;
  }
};

export function getChartViewIcon(chartView: ChartView) {
  switch (chartView) {
    case `line`:
      return <MdOutlineShowChart size={16} />;
    case `bar`:
      return <BsBarChart size={16} />;
    case `pie`:
      return <AiOutlinePieChart size={16} />;
    default:
      return null;
  }
}

export function VisualizationControls({
  activeView,
  availableBreakdownColumnIndexes,
  availableChartViews,
  availableXColumnIndexes,
  canShowChart,
  chartNumericColumnIndexes,
  getTooltip,
  headers,
  onSelectBreakdownColumnIndex,
  onSelectView,
  onSelectXColumnIndex,
  onSelectYColumnIndex,
  orderedColumnIndexes,
  selectedBreakdownColumnIndex,
  selectedXColumnIndex,
  selectedYColumnIndex,
}: VisualizationControlsProps) {
  return (
    <Controls>
      <ViewControls aria-label="Data visualization">
        {availableChartViews.map((chartView) => (
          <ViewButton
            key={chartView.value}
            type="button"
            $active={activeView === chartView.value}
            aria-pressed={activeView === chartView.value}
            aria-label={chartView.label}
            disabled={!canShowChart}
            title={getChartButtonTitle(canShowChart, chartView.label)}
            onClick={() => onSelectView(chartView.value)}
            style={{ order: getChartButtonPosition(chartView.value) }}
          >
            {getChartViewIcon(chartView.value)}
          </ViewButton>
        ))}
        <ViewButton
          type="button"
          $active={activeView === `table`}
          aria-pressed={activeView === `table`}
          aria-label="Table"
          title="Show table"
          onClick={() => onSelectView(`table`)}
          style={{ order: 100 }}
        >
          <CiViewTable size={16} />
        </ViewButton>
      </ViewControls>

      {activeView !== `table` && (
        <AxisControls>
          <AxisSegment>
            <VisuallyHidden>X axis</VisuallyHidden>
            <AxisLabelText>X: {headers[selectedXColumnIndex]}</AxisLabelText>
            <AxisTooltipWrapper onClick={(event) => event.stopPropagation()}>
              <ColumnTooltipIcon
                text={getTooltip?.(headers[selectedXColumnIndex]) ?? null}
              />
            </AxisTooltipWrapper>
            <AxisSelect
              aria-label="X axis"
              value={selectedXColumnIndex}
              onChange={(event) =>
                onSelectXColumnIndex(Number(event.target.value))
              }
            >
              {availableXColumnIndexes.map((columnIndex) => (
                <option key={columnIndex} value={columnIndex}>
                  {headers[columnIndex]}
                </option>
              ))}
            </AxisSelect>
          </AxisSegment>
          <AxisSegment>
            <VisuallyHidden>Y axis</VisuallyHidden>
            <AxisLabelText>Y: {headers[selectedYColumnIndex]}</AxisLabelText>
            <AxisTooltipWrapper onClick={(event) => event.stopPropagation()}>
              <ColumnTooltipIcon
                text={getTooltip?.(headers[selectedYColumnIndex]) ?? null}
              />
            </AxisTooltipWrapper>
            <AxisSelect
              aria-label="Y axis"
              value={selectedYColumnIndex}
              onChange={(event) =>
                onSelectYColumnIndex(Number(event.target.value))
              }
            >
              {orderedColumnIndexes
                .filter((columnIndex) =>
                  chartNumericColumnIndexes.includes(columnIndex),
                )
                .map((columnIndex) => (
                  <option key={columnIndex} value={columnIndex}>
                    {headers[columnIndex]}
                  </option>
                ))}
            </AxisSelect>
          </AxisSegment>
          {activeView !== `pie` && (
            <AxisSegment>
              <VisuallyHidden>Breakdown</VisuallyHidden>
              <AxisLabelText>
                Z:{` `}
                {selectedBreakdownColumnIndex >= 0
                  ? headers[selectedBreakdownColumnIndex]
                  : `None`}
              </AxisLabelText>
              <AxisSelect
                aria-label="Breakdown"
                value={selectedBreakdownColumnIndex}
                onChange={(event) =>
                  onSelectBreakdownColumnIndex(Number(event.target.value))
                }
              >
                <option value={-1}>None</option>
                {availableBreakdownColumnIndexes.map((columnIndex) => (
                  <option key={columnIndex} value={columnIndex}>
                    {headers[columnIndex]}
                  </option>
                ))}
              </AxisSelect>
            </AxisSegment>
          )}
        </AxisControls>
      )}
    </Controls>
  );
}
